import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSelectedAuthenticationOptions } from '../../../../../payments/hooks/useSelectedAuthenticationOptions/useSelectedAuthenticationOptions';
import {
  containsCredentials,
  createCredentials as createCredentialsImport,
  refreshCredentials as refreshCredentialsImport,
  updateCredentials as updateCredentialsImport,
} from '../../../../../connectivity/services/credentials';
import { Credentials, CredentialsEntity } from '../../../../../models/credentials';
import { applicationParameters } from '../../../../../utilities/application-parameters';

import { SubmitProviderInputsPayload } from '../../molecules/ProviderInputs/ProviderInputs';
import { AppState } from '../../../../../stores/redux-store';
import { RefreshableItem } from '../../../../../models/refreshableItem';
import {
  navigateUserToInitialTinkLinkAfterThirdPartyRedirect,
  shouldRedirectToReferPage,
} from '../../../../../models/configuration';
import { useConfiguration } from '../../../../ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { useSelectedProvider } from '../../../../App/ProviderContainer/useSelectedProvider';
import { getCredentialsAppUri } from '../../../../../utilities/app-uri/app-uri';
import { useProduct } from '../../../../ApplicationProviders/ProductProvider/ProductProvider';
import { getProviderRefreshableItems } from '../../../../../utilities/provider-utils';
import { CredentialsRequest } from '../../../../../lib-api/services/Credentials';

export type SubmitCredentialsPayload = {
  fields: SubmitProviderInputsPayload;
};

type SubmitCredentialsProps = {
  request: SubmitCredentialsPayload;
  credentials?: CredentialsEntity;
  refreshableItems?: RefreshableItem[];
  triggerRefresh?: boolean;
};

type UseSubmitCredentialsProps = {
  refreshCredentials?: typeof refreshCredentialsImport;
  createCredentials?: typeof createCredentialsImport;
  updateCredentials?: typeof updateCredentialsImport;
};

type UseSubmitCredentials = (props?: UseSubmitCredentialsProps) => {
  submitCredentials: (props: SubmitCredentialsProps) => void;
};

export const useSubmitCredentials: UseSubmitCredentials = ({
  refreshCredentials = refreshCredentialsImport,
  createCredentials = createCredentialsImport,
  updateCredentials = updateCredentialsImport,
}: UseSubmitCredentialsProps = {}) => {
  const { productType, productNames } = useProduct();
  const { data: configurationData } = useConfiguration();
  const dispatch = useDispatch();
  const { selectedProvider } = useSelectedProvider();
  const {
    userCredentials: { data: userCredentials },
  } = useSelector((state: AppState) => state);
  const selectedAuthenticationOptions = useSelectedAuthenticationOptions(selectedProvider);
  const callbackUri = applicationParameters.get('callbackUri');

  const submitCredentials = useCallback(
    ({
      credentials,
      refreshableItems,
      request: { fields },
      triggerRefresh,
    }: SubmitCredentialsProps) => {
      const credentialsRequest: CredentialsRequest = {
        fields,
        appUri: getCredentialsAppUri({
          redirectToReferPage: shouldRedirectToReferPage(configurationData),
          navigateToInitialTinkLinkRedirect:
            navigateUserToInitialTinkLinkAfterThirdPartyRedirect(configurationData),
        }),
        providerName: selectedProvider.name,
        callbackUri,
        productNames,
        ...selectedAuthenticationOptions,
        triggerRefresh,
      };

      const alreadyExistingCredentials = containsCredentials(userCredentials, credentialsRequest);

      if (credentials && Credentials.hasError(credentials)) {
        // We can only update credentials which are in ERROR state.
        // If we try to update credentials in CREATED or UPDATED state we get 400 response back because `username` field is immutable.
        dispatch(updateCredentials(credentials.id, credentialsRequest));

        return;
      }

      const calculatedRefreshableItems =
        refreshableItems ||
        getProviderRefreshableItems({
          provider: selectedProvider,
          productType,
          scope: applicationParameters.get('scope') || '',
          refreshableItems: applicationParameters.get('refreshableItems'),
        });

      if (alreadyExistingCredentials) {
        dispatch(
          refreshCredentials({
            appUri: getCredentialsAppUri({
              redirectToReferPage: shouldRedirectToReferPage(configurationData),
              navigateToInitialTinkLinkRedirect:
                navigateUserToInitialTinkLinkAfterThirdPartyRedirect(configurationData),
            }),
            credentials: alreadyExistingCredentials,
            authenticate: applicationParameters.get('authenticate'),
            refreshableItems: calculatedRefreshableItems,
            productNames,
            ...selectedAuthenticationOptions,
          })
        );

        return;
      }

      dispatch(
        createCredentials({
          credentialsRequest,
          refreshableItems: calculatedRefreshableItems,
          instantSign: true,
        })
      );
    },
    [
      configurationData,
      selectedProvider,
      callbackUri,
      productNames,
      selectedAuthenticationOptions,
      userCredentials,
      productType,
      dispatch,
      createCredentials,
      updateCredentials,
      refreshCredentials,
    ]
  );

  return { submitCredentials };
};
